import { TemplateGallerySkeleton } from "@/shared/ui";
import { useBusinessTypesQuery, useOnboardingBootstrap } from "@/features/onboarding";
import { StudioRouteSwitch } from "@/features/template-studio-host";

/**
 * Route entry for the store's template gallery.
 *
 * Its one job is resolving the store's business-type CODE and handing it to
 * StudioRouteSwitch. Vendors pick a business type during onboarding and the
 * gallery only ever shows templates for that type — legacy does this inside
 * the widget, but the embedded Studio can't: the editor token's `/context`
 * exposes onboarding *status* and the active business-type catalogue, yet
 * nothing that says which type THIS store chose (and its catalogue entries
 * carry no id to map against). So the host resolves it and passes it into the
 * frame. Backend has been asked to put the store's own code on `/context`; once
 * that ships the Studio can read it directly and this can go back to being a
 * bare `<StudioRouteSwitch>`.
 *
 * The resolution lives here rather than in the frame because
 * `features/template-studio-host` may not import `features/onboarding` —
 * same-layer imports are forbidden — while `pages` may import features.
 */
export default function TemplatesRoutePage() {
  const { data: bootstrap, isLoading: bootstrapLoading } = useOnboardingBootstrap();
  const businessTypeId = bootstrap?.data?.onboarding?.businessTypeId ?? null;
  const { data: businessTypesData, isLoading: businessTypesLoading } =
    useBusinessTypesQuery({ enabled: !!businessTypeId });
  const businessTypeCode = businessTypeId
    ? businessTypesData?.data?.find((b) => b.id === businessTypeId)?.code
    : undefined;

  // Both queries share their keys with the onboarding wizard, so a warm cache
  // resolves synchronously and nothing is delayed. Cold, we wait rather than
  // boot the frame with no filter — the Studio reads the code from its URL at
  // first render, so a late arrival would mean loading the unfiltered gallery
  // and then reloading it.
  //
  // The gallery-shaped skeleton, not the generic page one: this is the second
  // of up to three host loading states on this route (lazy chunk → here →
  // iframe boot) and they all have to look like the same single wait.
  const resolving = bootstrapLoading || (!!businessTypeId && businessTypesLoading);
  if (resolving) return <TemplateGallerySkeleton />;

  return (
    <StudioRouteSwitch
      studioPath="/templates"
      titleKey="templates.studio.galleryTitle"
      fill="chrome"
      businessTypeCode={businessTypeCode}
      data-cy="studio-gallery-frame"
    />
  );
}
